import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import React from 'react';
import type { SelectOption } from '../registry';

interface SettingsSegmentedControlProps {
  /** Stable name used to build per-option testIDs (`option-<name>-<value>`). */
  name: string;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  /** The setting's label, prefixed onto each option for screen readers. */
  groupLabel?: string;
}

/**
 * A segmented control built on the design-system `Tabs` primitive. Used to render
 * `select` settings (Units, Default Landing Screen) as a compact inline choice.
 *
 * Elevation follows the tonal ladder: the track is an inset `surface-secondary`
 * well and the selected segment steps *up* to `surface-hover` so the choice reads
 * as a raised pill — not the recessed hole a `card`-colored pill produced against
 * the card the row already sits on.
 */
export function SettingsSegmentedControl({
  name,
  value,
  options,
  onChange,
  disabled = false,
  groupLabel,
}: SettingsSegmentedControlProps) {
  return (
    <Tabs
      value={value}
      onValueChange={disabled ? undefined : onChange}
      className="mt-2"
    >
      <TabsList className="border-b-0 ps-0 gap-1 rounded-lg bg-surface-secondary p-1">
        {options.map((option) => (
          <TabsTrigger
            key={option.value}
            testID={`option-${name}-${option.value}`}
            value={option.value}
            title={option.label}
            disabled={disabled}
            accessibilityLabel={
              groupLabel ? `${groupLabel}: ${option.label}` : option.label
            }
            activeClassName="rounded-md bg-surface-hover"
            activeTextClassName="text-foreground font-semibold"
            className="min-h-11 active:opacity-80"
          />
        ))}
      </TabsList>
    </Tabs>
  );
}
